import { useState, useEffect } from "react";
import axios from "axios";
import { useVisitor } from "@/App";
import Header from "@/components/Header";
import SportFilter from "@/components/SportFilter";
import VideoCard from "@/components/VideoCard";
import VideoDetailModal from "@/components/VideoDetailModal";
import { Clapperboard, Loader2 } from "lucide-react";

export default function ShortClips() {
  const { API } = useVisitor();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSport, setSelectedSport] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    fetchVideos();
  }, [selectedSport]);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const params = { video_type: "short_clip" };
      if (selectedSport) params.sport = selectedSport;
      const { data } = await axios.get(`${API}/videos`, { params });
      setVideos(data);
    } catch (err) {
      console.error("Failed to fetch videos", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="short-clips-page">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Clapperboard className="w-6 h-6 text-[#FF5A00]" />
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A1128] tracking-tight" style={{ fontFamily: 'Outfit' }}>
              Short Clips
            </h1>
          </div>
          <p className="text-sm text-[#0A1128]/50 ml-9">
            Pre-cut highlight moments from different sports
          </p>
        </div>

        {/* Sport Filter */}
        <div className="mb-8">
          <SportFilter selected={selectedSport} onSelect={setSelectedSport} />
        </div>

        {/* Video Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-spinner">
            <Loader2 className="w-8 h-8 text-[#FF5A00] animate-spin" />
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-state">
            <Clapperboard className="w-16 h-16 text-[#0A1128]/10 mx-auto mb-4" />
            <p className="text-lg font-medium text-[#0A1128]/40">No short clips available</p>
            <p className="text-sm text-[#0A1128]/30 mt-1">Check back later for new content</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="video-grid">
            {videos.map((video, idx) => (
              <div
                key={video.id}
                className="animate-fade-in-up"
                style={{ animationDelay: `${idx * 80}ms` }}
              >
                <VideoCard video={video} onClick={setSelectedVideo} />
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Video Detail Modal */}
      <VideoDetailModal
        video={selectedVideo}
        open={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
      />
    </div>
  );
}
